import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { DocManiaLogo, DocManiaLogoImage } from '../components/DocManiaLogo';
import { Mail, Lock, User, Globe, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REGISTRATION_CLOSED_DEFAULT =
  'Registration is currently closed. Public access opens in July 2026.';

const WAITLIST_DEFAULT =
  "DocMania is opening internationally in August 2026 — you're on the list! We'll email you the moment registration opens in your region.";

const COUNTRIES = [
  { code: "NG", name: "Nigeria" },
  { code: "GH", name: "Ghana" },
  { code: "SN", name: "Senegal" },
  { code: "KE", name: "Kenya" },
  { code: "ZA", name: "South Africa" },
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "IN", name: "India" },
  { code: "AE", name: "United Arab Emirates" },
];

export default function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [country, setCountry] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationClosed, setRegistrationClosed] = useState(null);
  const [waitlistMessage, setWaitlistMessage] = useState(null);
  const [waitlistJoined, setWaitlistJoined] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/config-status`);
        if (res.data?.registration_open === false) {
          setRegistrationClosed(REGISTRATION_CLOSED_DEFAULT);
        }
      } catch {
        // If config check fails, allow form; register will enforce the gate.
      } finally {
        setConfigLoading(false);
      }
    })();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!country) {
      toast.error('Please select your country');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, {
        email,
        password,
        full_name: fullName,
        country
      });

      if (response.data.waitlisted) {
        setWaitlistJoined(true);
        setWaitlistMessage(response.data.message || WAITLIST_DEFAULT);
        toast.success("You're on the waitlist — we'll email you when we launch.");
        return;
      }

      if (response.data.success) {
        toast.success('Account created! Check your email to verify your account.');
        navigate('/verify-email');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        const msg = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || d).join(' ')
            : REGISTRATION_CLOSED_DEFAULT;
        setRegistrationClosed(msg);
        return;
      }
      const msg = error.response?.data?.detail || 'Registration failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-brand-hero">
      <Card className="w-full max-w-md card-elevated animate-fade-in">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <DocManiaLogoImage size={56} className="shadow-md" />
          </div>
          <div className="space-y-2">
            <CardTitle className="text-2xl font-bold">
              {waitlistJoined ? 'You\'re on the list' : 'Create Account'}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {waitlistJoined ? (
                'We\'ll notify you when DocMania opens in your region.'
              ) : (
                <>Join <DocManiaLogo size="small" /> today</>
              )}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {configLoading ? (
            <div className="flex justify-center py-10" data-testid="signup-config-loading">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : registrationClosed ? (
            <div className="space-y-6 py-4 text-center" data-testid="signup-closed-message">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {registrationClosed}
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="signup-closed-login-btn">
                  Sign in to an existing account
                </Button>
              </Link>
            </div>
          ) : waitlistJoined ? (
            <div className="space-y-6 py-4 text-center" data-testid="signup-waitlist-joined">
              <p className="text-sm text-muted-foreground leading-relaxed">
                {waitlistMessage || WAITLIST_DEFAULT}
              </p>
              <p className="text-sm text-foreground">
                We saved <span className="font-medium">{email}</span> for launch updates.
              </p>
              <Link to="/login">
                <Button variant="outline" className="w-full" data-testid="signup-waitlist-login-btn">
                  Sign in to an existing account
                </Button>
              </Link>
            </div>
          ) : (
          <>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="fullName"
                  data-testid="signup-fullname-input"
                  type="text"
                  placeholder="John Doe"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="signup-email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="signup-password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10"
                  required
                  minLength={6}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="signup-country-select" className="w-full">
                  <Globe className="h-4 w-4 text-muted-foreground mr-2" />
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRIES.map((c) => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              type="submit"
              data-testid="signup-submit-btn"
              className="w-full glow-button"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
          
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
              Sign in
            </Link>
          </div>

          <p className="mt-4 text-center text-xs text-muted-foreground leading-relaxed" data-testid="signup-legal-text">
            By continuing, you agree to our{' '}
            <a href="https://www.docmania.co/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="signup-terms-link">Terms &amp; Conditions</a>
            {' '}and{' '}
            <a href="https://www.docmania.co/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="signup-privacy-link">Privacy Policy</a>.
          </p>
          </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
